import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { isApiError } from '@/api/errors';
import { createDirectOrg, directPlanOptions, listDirectOrgs } from '@/api/orgs';
import { useCan } from '@/auth/session';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PageHeader } from '@/ui/PageHeader';
import { QueryError } from '@/ui/QueryError';
import { OrgTable } from './OrgTable';

export function DirectOrgsPage() {
  const can = useCan();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);

  const query = useQuery({
    queryKey: ['direct-orgs', { q, page }],
    queryFn: () => listDirectOrgs({ ...(q ? { q } : {}), page }),
  });

  return (
    <>
      <PageHeader
        title="Direct organizations"
        description="WaSparks's own customers, under the SYSTEM partner."
        actions={
          can('directOrgs.create') && (
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button>New direct org</Button>
              </DialogTrigger>
              <CreateDirectOrgDialog key={String(createOpen)} onDone={() => setCreateOpen(false)} />
            </Dialog>
          )
        }
      />

      <Input
        aria-label="Search direct organizations"
        placeholder="Search by name, reference or id"
        className="max-w-xs"
        value={q}
        onChange={(event) => {
          setPage(0);
          setQ(event.target.value);
        }}
      />

      {query.isError ? (
        <QueryError error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <OrgTable
          page={query.data}
          isLoading={query.isPending}
          onPage={setPage}
          empty="No direct organizations yet."
        />
      )}
    </>
  );
}

const schema = z.object({
  name: z.string().trim().min(1, 'Name is required.').max(200, 'Name is too long.'),
  external_ref: z.string().trim().max(200, 'Reference is too long.'),
  plan_id: z.string().min(1, 'Choose a plan.'),
  timezone: z.string().trim().min(1, 'Timezone is required.'),
});
type Form = z.infer<typeof schema>;

function CreateDirectOrgDialog({ onDone }: { onDone: () => void }) {
  const queryClient = useQueryClient();
  const plans = useQuery({ queryKey: ['direct-orgs', 'plan-options'], queryFn: directPlanOptions });
  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<Form>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', external_ref: '', plan_id: '', timezone: 'UTC' },
  });

  const create = useMutation({
    mutationFn: (values: Form) =>
      createDirectOrg({ ...values, external_ref: values.external_ref || null }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['direct-orgs'] });
      void queryClient.invalidateQueries({ queryKey: ['orgs'] });
      onDone();
    },
    onError: (error) => {
      if (!isApiError(error)) return;
      for (const { field, message } of error.fieldErrors) {
        if (field in schema.shape) setError(field as keyof Form, { message });
      }
    },
  });

  const general =
    create.isError && isApiError(create.error) && create.error.fieldErrors.length === 0
      ? create.error.message
      : null;

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>New direct organization</DialogTitle>
      </DialogHeader>
      {general && (
        <Alert variant="destructive">
          <AlertDescription>{general}</AlertDescription>
        </Alert>
      )}
      <form id="create-direct-org" noValidate onSubmit={handleSubmit((v) => create.mutate(v))}>
        <FieldGroup>
          <Field data-invalid={Boolean(errors.name)}>
            <FieldLabel htmlFor="org-name">Name</FieldLabel>
            <Input id="org-name" autoFocus {...register('name')} />
            <FieldError errors={[errors.name]} />
          </Field>
          <Field data-invalid={Boolean(errors.external_ref)}>
            <FieldLabel htmlFor="org-ref">External reference (optional)</FieldLabel>
            <Input id="org-ref" {...register('external_ref')} />
            <FieldError errors={[errors.external_ref]} />
          </Field>
          <Field data-invalid={Boolean(errors.plan_id)}>
            <FieldLabel htmlFor="org-plan">Plan</FieldLabel>
            <Controller
              control={control}
              name="plan_id"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="org-plan">
                    <SelectValue placeholder="Choose a plan" />
                  </SelectTrigger>
                  <SelectContent>
                    {plans.data?.map((plan) => (
                      <SelectItem key={plan.id} value={plan.id}>
                        {plan.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            <FieldError errors={[errors.plan_id]} />
          </Field>
          <Field data-invalid={Boolean(errors.timezone)}>
            <FieldLabel htmlFor="org-tz">Timezone (IANA)</FieldLabel>
            <Input id="org-tz" placeholder="Europe/London" {...register('timezone')} />
            <FieldError errors={[errors.timezone]} />
          </Field>
        </FieldGroup>
      </form>
      <DialogFooter>
        <Button type="submit" form="create-direct-org" disabled={create.isPending}>
          {create.isPending ? 'Creating…' : 'Create organization'}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
